import React, { useState, useEffect, useRef } from 'react';
import { Vote, Users, Plus, X, Play, Pause, SkipForward, Award, Mic, Settings, CheckCircle } from 'lucide-react';
import ChairScript from '../meeting/ChairScript';
import ContextHelpTip from '../common/ContextHelpTip';
import {
  buildTourDemoBallotVotes,
  buildTourDemoCurrentElection,
  buildTourDemoElectionSetup,
  buildTourDemoNominees,
  buildTourDemoSpeechNominees,
} from '../../tours/tourDemoData';
import {
  getElectionChairScript,
  getElectionOfficeLabel,
  getElectionWinners,
} from '../../utils/electionChairScript';

const ElectionsInterface = ({
  theme,
  meetingSetup,
  elections,
  setElections,
  currentElection,
  setCurrentElection,
  candidateSpeechQueue,
  setCandidateSpeechQueue,
  currentCandidate,
  setCurrentCandidate,
  speakerTimeLimit,
  setCandidateSpeechTimeLimit,
  inElectionMode,
  setInElectionMode,
  currentSpeaker,
  setCurrentSpeaker,
  speakerTime,
  setSpeakerTime,
  isTimerRunning,
  setIsTimerRunning,
  onEndElections,
  onAddAgendaEntry,
  electionScreen,
  setElectionScreen,
  electionNominees,
  setElectionNominees,
  electionBallotVotes,
  setElectionBallotVotes,
  electionWriteInCandidates,
  setElectionWriteInCandidates,
  electionInvalidBallots,
  setElectionInvalidBallots,
  onViewChange,
  remoteVoteSession = null,
  remoteVoteTally = null,
  remoteVoteBusy = false,
  remoteVoteError = null,
  onOpenRemoteElectionBallot = null,
  onCloseRemoteVote = null,
  onUseRemoteElectionTally = null,
}) => {
  // Election setup state
  const [electionSetup, setElectionSetup] = useState({
    office: '',
    speechTimeLimit: speakerTimeLimit || 3,
    secretBallot: true,
    allowWriteIns: true,
    showVoteBreakdown: false,
    seatsToFill: 1
  });

  // When the remote ballot transitions from open to closed, pull the final
  // tallies in automatically — closing the ballot should never leave the
  // chair unable to retrieve the final numbers.
  const prevRemoteVoteStatusRef = useRef(remoteVoteSession?.status);
  useEffect(() => {
    const prevStatus = prevRemoteVoteStatusRef.current;
    prevRemoteVoteStatusRef.current = remoteVoteSession?.status;
    if (
      prevStatus === 'open' &&
      remoteVoteSession?.status === 'closed' &&
      remoteVoteSession?.kind === 'election'
    ) {
      onUseRemoteElectionTally?.();
    }
  }, [remoteVoteSession?.status, remoteVoteSession?.kind, onUseRemoteElectionTally]);

  // Nominations state
  const [newNominee, setNewNominee] = useState('');
  const [nominatorName, setNominatorName] = useState('');

  const cardClass =
    theme === 'dark'
      ? 'bg-gray-800 text-white border border-gray-600'
      : 'bg-pp-surface text-pp-ink border border-pp-line/80';
  const accentClass = theme === 'dark' ? 'bg-gray-700/80' : 'bg-pp-paper/70';
  const officeLabel = getElectionOfficeLabel(currentElection, electionSetup.office);

  // Guided tour: preview later stages with sample nominees/votes
  useEffect(() => {
    const onTourScreen = (event) => {
      const screen = Number(event?.detail?.screen);
      if (!(screen >= 1 && screen <= 5)) return;

      const setup = buildTourDemoElectionSetup();
      setElectionSetup(setup);
      // Every screen (including 1, setup) needs this so App.js's auto-switch effect
      // actually mounts the Elections view — otherwise the tour highlights elements
      // that were never rendered in the first place.
      setInElectionMode?.(true);

      if (screen === 1) {
        setElectionScreen(1);
        return;
      }

      const election = buildTourDemoCurrentElection(setup.office);
      setCurrentElection?.(election);
      setInElectionMode?.(true);

      if (screen === 2) {
        setElectionNominees(buildTourDemoNominees());
        setElectionScreen(2);
        return;
      }

      if (screen === 3) {
        const speechNominees = buildTourDemoSpeechNominees();
        setElectionNominees(speechNominees);
        setCurrentCandidate?.(speechNominees.find((n) => !n.hasSpoken) || speechNominees[0] || null);
        setSpeakerTime?.(setup.speechTimeLimit * 60);
        setIsTimerRunning?.(false);
        setElectionScreen(3);
        return;
      }

      const ballotNominees = buildTourDemoSpeechNominees().map((n) => ({ ...n, hasSpoken: true }));
      setElectionNominees(ballotNominees);
      setElectionBallotVotes(buildTourDemoBallotVotes());
      setElectionWriteInCandidates([]);
      setElectionInvalidBallots(0);
      setCurrentCandidate?.(null);

      if (screen === 4) {
        setElectionScreen(4);
        return;
      }

      // screen 5 — results
      setElectionScreen(5);
    };
    window.addEventListener('pp-tour-election-screen', onTourScreen);
    return () => window.removeEventListener('pp-tour-election-screen', onTourScreen);
  }, [
    setElectionScreen,
    setElectionNominees,
    setElectionBallotVotes,
    setElectionWriteInCandidates,
    setElectionInvalidBallots,
    setCurrentElection,
    setInElectionMode,
    setCurrentCandidate,
    setSpeakerTime,
    setIsTimerRunning,
  ]);

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === null || seconds === undefined) {
      seconds = 0;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Breadcrumb navigation
  const breadcrumbs = [
    { id: 1, label: 'Setup', completed: electionScreen > 1 },
    { id: 2, label: 'Nominations', completed: electionScreen > 2 },
    { id: 3, label: 'Speeches', completed: electionScreen > 3 },
    { id: 4, label: 'Voting', completed: electionScreen > 4 },
    { id: 5, label: 'Results', completed: electionScreen > 5 }
  ];

  // Chair script for each screen
  const getChairScript = () =>
    getElectionChairScript({
      electionScreen,
      officeLabel,
      electionNominees,
      currentCandidate,
      speechTimeLimit: electionSetup.speechTimeLimit,
      secretBallot: electionSetup.secretBallot,
      showVoteBreakdown: Boolean(
        currentElection?.showVoteBreakdown ?? electionSetup.showVoteBreakdown
      ),
      electionWriteInCandidates,
      electionBallotVotes,
      seatsToFill: electionSetup.seatsToFill || currentElection?.seatsToFill || 1,
    });

  // Start election from setup screen
  const startElection = () => {
    if (!electionSetup.office.trim()) return;

    setCandidateSpeechTimeLimit(electionSetup.speechTimeLimit);
    setSpeakerTime(electionSetup.speechTimeLimit * 60);

    const election = {
      id: Date.now(),
      office: electionSetup.office,
      speechTimeLimit: electionSetup.speechTimeLimit,
      secretBallot: electionSetup.secretBallot,
      allowWriteIns: electionSetup.allowWriteIns,
      showVoteBreakdown: electionSetup.showVoteBreakdown,
      seatsToFill: electionSetup.seatsToFill,
      status: 'nominations_open',
      timestamp: new Date().toLocaleTimeString(),
      electionNominees: []
    };

    setCurrentElection(election);
    setInElectionMode(true);
    setElectionScreen(2);

    onAddAgendaEntry({
      id: Date.now(),
      text: `Election started for ${electionSetup.office}`,
      timestamp: new Date().toLocaleTimeString(),
      type: 'election'
    });
  };

  // Add nominee in nominations screen
  // Handle Enter key press for forms
  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      action();
    }
  };

  const addNominee = () => {
    if (!newNominee.trim() || !nominatorName.trim()) return;

    const nominee = {
      id: Date.now() + Math.random(),
      name: newNominee.trim(),
      nominatedBy: nominatorName.trim(),
      status: 'pending', // pending, accepted, declined
      hasSpoken: false,
      votes: 0
    };

    setElectionNominees([...electionNominees, nominee]);
    setNewNominee('');
    setNominatorName('');

    // Return focus to nominee name field
    setTimeout(() => {
      const nomineeInput = document.querySelector('input[placeholder="Enter nominee name"]');
      if (nomineeInput) {
        nomineeInput.focus();
      }
    }, 100);
  };

  // Update nominee status
  const updateNomineeStatus = (nomineeId, status) => {
    setElectionNominees(electionNominees.map(n =>
      n.id === nomineeId ? { ...n, status } : n
    ));
  };

  // Remove nominee
  const removeNominee = (nomineeId) => {
    setElectionNominees(electionNominees.filter(n => n.id !== nomineeId));
  };

  // Begin speeches
  const beginSpeeches = () => {
    const acceptedNominees = electionNominees.filter(n => n.status === 'accepted');
    if (acceptedNominees.length === 0) return;

    setCandidateSpeechQueue(acceptedNominees);
    setElectionScreen(3);
  };

  // Speech controls
  const startCandidateSpeech = (candidate) => {
    setCurrentCandidate(candidate);
    setCurrentSpeaker(candidate.name);
    setSpeakerTime(electionSetup.speechTimeLimit * 60);
    setIsTimerRunning(true);
  };

  const stopCandidateSpeech = () => {
    setIsTimerRunning(false);
    if (currentCandidate) {
      setElectionNominees(electionNominees.map(n =>
        n.id === currentCandidate.id ? { ...n, hasSpoken: true } : n
      ));
      setCandidateSpeechQueue(candidateSpeechQueue.filter(c => c.id !== currentCandidate.id));
    }
    setCurrentCandidate(null);
    setCurrentSpeaker(null);
    setSpeakerTime(0);
  };

  const nextCandidate = () => {
    stopCandidateSpeech();
    // Check if there are more candidates who haven't spoken
    const remainingCandidates = candidateSpeechQueue.filter(c => c.id !== currentCandidate?.id);
    if (remainingCandidates.length > 0) {
      const nextCandidate = remainingCandidates[0];
      startCandidateSpeech(nextCandidate);
    }
    // If no more candidates, speaking ends (timer stays at 0)
  };

  // Begin voting
  const beginVoting = () => {
    setElectionScreen(4);
    // Initialize ballot votes for all accepted electionNominees
    const acceptedNominees = electionNominees.filter(n => n.status === 'accepted');
    const initialVotes = {};
    acceptedNominees.forEach(nominee => {
      initialVotes[nominee.id] = 0;
    });
    setElectionBallotVotes(initialVotes);
  };

  // Voting controls
  const handleVoteChange = (nomineeId, votes) => {
    setElectionBallotVotes(prev => ({
      ...prev,
      [nomineeId]: parseInt(votes) || 0
    }));
  };

  const addWriteInCandidate = () => {
    const name = prompt('Enter write-in candidate name:');
    const votes = prompt('Enter number of votes for this candidate:');

    if (name && votes) {
      setElectionWriteInCandidates([...electionWriteInCandidates, {
        id: Date.now() + Math.random(),
        name: name.trim(),
        votes: parseInt(votes) || 0
      }]);
    }
  };

  // Complete election
  const completeElection = () => {
    // Update electionNominees with vote counts
    const updatedNominees = electionNominees.map(nominee => ({
      ...nominee,
      votes: electionBallotVotes[nominee.id] || 0
    }));

    // Calculate total votes
    const totalNomineeVotes = Object.values(electionBallotVotes).reduce((sum, votes) => sum + votes, 0);
    const totalWriteInVotes = electionWriteInCandidates.reduce((sum, candidate) => sum + candidate.votes, 0);
    const totalVotes = totalNomineeVotes + totalWriteInVotes + electionInvalidBallots;

    // Find winner
    const allCandidates = [
      ...updatedNominees.map(n => ({ name: n.name, votes: n.votes })),
      ...electionWriteInCandidates
    ];
    const maxVotes = Math.max(...allCandidates.map(c => c.votes));
    const winners = allCandidates.filter(c => c.votes === maxVotes);
    const winner = winners.length === 1 ? winners[0] : null;

    // Create results array for export compatibility
    const allCandidatesWithVotes = [
      ...updatedNominees.filter(n => n.status === 'accepted').map(n => ({
        candidate: n.name,
        votes: n.votes || 0,
        percentage: totalVotes > 0 ? ((n.votes || 0) / totalVotes * 100).toFixed(1) : '0.0'
      })),
      ...electionWriteInCandidates.map(w => ({
        candidate: `${w.name} (Write-in)`,
        votes: w.votes || 0,
        percentage: totalVotes > 0 ? ((w.votes || 0) / totalVotes * 100).toFixed(1) : '0.0'
      }))
    ].sort((a, b) => b.votes - a.votes); // Sort by votes descending

    // Update election
    const completedElection = {
      ...currentElection,
      status: 'completed',
      electionNominees: updatedNominees,
      electionWriteInCandidates,
      electionInvalidBallots,
      totalVotes,
      winner: winner?.name || null,
      winnerVotes: winner?.votes || 0,
      showVoteBreakdown: electionSetup.showVoteBreakdown,
      results: allCandidatesWithVotes // Add results array for export function
    };

    setCurrentElection(completedElection);
    setElections([...elections, completedElection]);
    setElectionNominees(updatedNominees);
    setElectionScreen(5);

    const officeForMinutes = ((currentElection?.office ?? electionSetup.office) || '').trim() || 'office';

    // Add to minutes - create different versions for Observer View and exports
    const baseText = winner
      ? `Election complete: ${winner.name} elected as ${officeForMinutes}`
      : `Election complete: No clear winner for ${officeForMinutes}`;

    const observerViewText = electionSetup.showVoteBreakdown && winner
      ? `${baseText} with ${winner.votes} votes`
      : baseText;

    // For exported minutes, always include full details
    const exportText = winner
      ? `Election complete: ${winner.name} elected as ${officeForMinutes} with ${winner.votes} votes`
      : `Election complete: No clear winner for ${officeForMinutes}`;

    onAddAgendaEntry({
      id: Date.now(),
      text: observerViewText,
      exportText: exportText, // Full details for export
      timestamp: new Date().toLocaleTimeString(),
      type: 'election',
      showVoteBreakdown: electionSetup.showVoteBreakdown
    });
  };

  // Get election winner
  const getElectionWinnersLocal = () =>
    getElectionWinners({
      electionScreen,
      electionNominees,
      electionWriteInCandidates,
      electionBallotVotes,
      seatsToFill: electionSetup.seatsToFill || currentElection?.seatsToFill || 1,
    });

  // Legacy single winner function for backwards compatibility
  const getElectionWinner = () => {
    const winners = getElectionWinnersLocal();
    return winners.length > 0 ? winners[0] : null;
  };

  // Reset election
  const resetElection = () => {
    setCurrentElection(null);
    setInElectionMode(false);
    setElectionScreen(1);
    setElectionSetup({
      office: '',
      speechTimeLimit: 3,
      secretBallot: true,
      allowWriteIns: true,
      showVoteBreakdown: false,
      seatsToFill: 1
    });
    setElectionNominees([]);
    setElectionBallotVotes({});
    setElectionWriteInCandidates([]);
    setElectionInvalidBallots(0);
    setCandidateSpeechQueue([]);
    setCurrentCandidate(null);
    setCurrentSpeaker(null);
    setSpeakerTime(0);
    setIsTimerRunning(false);
  };

  // Return to meeting after election is complete
  const returnToMeeting = () => {
    // Reset election state
    resetElection();
    // Return to meeting view and sync with observer windows
    if (onViewChange) {
      onViewChange('meeting');
    }
  };

  return (
    <div className={`min-h-screen p-6 ${theme === 'dark' ? 'bg-gray-900' : 'bg-pp-paper'}`} data-tour="elections-panel">
      <div className="mx-auto max-w-6xl">

        {/* Header */}
        <div className={`${cardClass} mb-6 rounded-xl p-6 shadow-pp`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`flex items-center gap-3 text-3xl font-bold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-pp-ink'}`}>
                <Vote size={32} className="text-pp-accent" />
                Elections
              </h1>
              {meetingSetup.organizationName && (
                <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-pp-muted'}`}>{meetingSetup.organizationName}</p>
              )}
            </div>
            {electionScreen > 1 && (
              <div className="text-right">
                <div className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-pp-ink'}`}>{officeLabel}</div>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-pp-muted'}`}>Election in progress</div>
              </div>
            )}
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        {electionScreen > 1 && (
          <div className={`${cardClass} mb-6 rounded-xl p-4 shadow-pp`} data-tour="elections-breadcrumbs">
            <div className="flex items-center justify-between">
              {breadcrumbs.map((crumb, index) => (
                <div key={crumb.id} className="flex items-center">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-medium ${
                    electionScreen === crumb.id ? 'border-pp-accent bg-pp-accent text-white' :
                    crumb.completed ? 'border-pp-sage bg-pp-sage text-white' :
                    'border-pp-line bg-pp-paper text-pp-muted'
                  }`}>
                    {crumb.completed ? <CheckCircle size={16} /> : crumb.id}
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    electionScreen === crumb.id ? 'text-pp-accent' :
                    crumb.completed ? 'text-pp-sage' :
                    'text-pp-muted'
                  }`}>
                    {crumb.label}
                  </span>
                  {index < breadcrumbs.length - 1 && (
                    <div className={`mx-4 h-0.5 w-12 ${
                      crumb.completed ? 'bg-pp-sage' : 'bg-pp-line'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chair's Script */}
        <div className="mb-6" data-tour="elections-chair-script">
          <ChairScript chairMessage={getChairScript()} />
        </div>

        {/* Screen 1: Election Setup */}
        {electionScreen === 1 && (
          <div className={`${cardClass} rounded-xl shadow-pp p-6`} data-tour="elections-setup">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Settings size={20} className="text-pp-accent" />
              Start New Election
            </h2>

            <div className="max-w-2xl space-y-6">
              <div data-tour="elections-office">
                <label className="block text-sm font-medium mb-2">Office/Position *</label>
                <input
                  type="text"
                  value={electionSetup.office}
                  onChange={(e) => setElectionSetup(prev => ({ ...prev, office: e.target.value }))}
                  onKeyDown={(e) => handleKeyDown(e, startElection)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="e.g., President, Vice President, Board Member"
                />
              </div>

              <div data-tour="elections-speech-limit">
                <label className="block text-sm font-medium mb-2">Speech Time Limit (minutes)</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={electionSetup.speechTimeLimit}
                  onChange={(e) => setElectionSetup(prev => ({ ...prev, speechTimeLimit: parseInt(e.target.value) || 3 }))}
                  className="w-full p-3 border rounded-lg"
                />
              </div>

              <div data-tour="elections-seats">
                <label className="block text-sm font-medium mb-2">Number of Seats to Fill</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={electionSetup.seatsToFill}
                  onChange={(e) => setElectionSetup(prev => ({ ...prev, seatsToFill: parseInt(e.target.value) || 1 }))}
                  className="w-full p-3 border rounded-lg"
                />
              </div>

              <div className="space-y-3" data-tour="elections-options">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="secretBallot"
                    checked={electionSetup.secretBallot}
                    onChange={(e) => setElectionSetup(prev => ({ ...prev, secretBallot: e.target.checked }))}
                    className="mr-3"
                  />
                  <label htmlFor="secretBallot" className="text-sm font-medium">Secret Ballot Required</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="allowWriteIns"
                    checked={electionSetup.allowWriteIns}
                    onChange={(e) => setElectionSetup(prev => ({ ...prev, allowWriteIns: e.target.checked }))}
                    className="mr-3"
                  />
                  <label htmlFor="allowWriteIns" className="text-sm font-medium">Allow Write-in Candidates</label>
                </div>

                <div className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    id="showVoteBreakdown"
                    checked={electionSetup.showVoteBreakdown}
                    onChange={(e) => setElectionSetup(prev => ({ ...prev, showVoteBreakdown: e.target.checked }))}
                    className="mr-3"
                  />
                  <label htmlFor="showVoteBreakdown" className="text-sm font-medium">Show Vote Breakdown on Observer Screen</label>
                  <ContextHelpTip
                    text="RONR calls for meeting totals only to be entered into the minutes, not announced to the Assembly"
                    theme={theme}
                  />
                </div>
              </div>

              <button
                data-tour="elections-start"
                onClick={startElection}
                disabled={!electionSetup.office.trim()}
                className="w-full px-6 py-3 bg-pp-accent text-white rounded-lg hover:bg-pp-accentHover disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Start Election
              </button>
            </div>
          </div>
        )}

        {/* Screen 2: Nominations */}
        {electionScreen === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Add Nominations */}
            <div className={`${cardClass} rounded-xl shadow-pp p-6`} data-tour="elections-nominate-form">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Plus size={20} className="text-pp-accent" />
                Open Nominations
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nominee Name</label>
                  <input
                    type="text"
                    value={newNominee}
                    onChange={(e) => setNewNominee(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, addNominee)}
                    className="w-full p-3 border rounded-lg"
                    placeholder="Enter nominee name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Nominated By</label>
                  <input
                    type="text"
                    value={nominatorName}
                    onChange={(e) => setNominatorName(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, addNominee)}
                    className="w-full p-3 border rounded-lg"
                    placeholder="Enter nominator name"
                  />
                </div>

                <button
                  onClick={addNominee}
                  disabled={!newNominee.trim() || !nominatorName.trim()}
                  className="w-full px-4 py-2 bg-pp-accent text-white rounded-lg hover:bg-pp-accentHover disabled:opacity-50"
                >
                  Add Nomination
                </button>

                <div className="pt-4 border-t">
                  <button
                    data-tour="elections-close-noms"
                    onClick={beginSpeeches}
                    disabled={electionNominees.filter(n => n.status === 'accepted').length === 0}
                    className="w-full rounded-lg bg-pp-sage px-4 py-3 font-medium text-white hover:opacity-90 disabled:opacity-50"
                  >
                    Close Nominations & Begin Speeches
                  </button>
                </div>
              </div>
            </div>

            {/* Nominees List */}
            <div className={`${cardClass} rounded-xl shadow-pp p-6`} data-tour="elections-nominees-list">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Users size={20} className="text-pp-accent" />
                Nominees ({electionNominees.filter(n => n.status === 'accepted').length} accepted)
              </h3>

              {electionNominees.length === 0 ? (
                <div className="text-center py-8">
                  <Users size={48} className="text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No nominations yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {electionNominees.map((nominee) => (
                    <div key={nominee.id} className={`rounded-lg border p-3 ${
                      nominee.status === 'accepted' ? 'border-pp-sage/30 bg-pp-sageSoft' :
                      nominee.status === 'declined' ? 'border-red-200 bg-red-50' :
                      `${accentClass} border-pp-line/80`
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-medium">{nominee.name}</span>
                          <span className="text-sm text-gray-600 ml-2">by {nominee.nominatedBy}</span>
                        </div>
                        <button
                          onClick={() => removeNominee(nominee.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X size={16} />
                        </button>
                      </div>

                      {nominee.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateNomineeStatus(nominee.id, 'accepted')}
                            className="rounded bg-pp-sage px-3 py-1 text-sm text-white hover:opacity-90"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => updateNomineeStatus(nominee.id, 'declined')}
                            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                          >
                            Decline
                          </button>
                        </div>
                      )}

                      {nominee.status === 'accepted' && (
                        <span className="text-sm text-green-600 font-medium">✓ Accepted</span>
                      )}

                      {nominee.status === 'declined' && (
                        <span className="text-sm text-red-600 font-medium">✗ Declined</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Screen 3: Speeches */}
        {electionScreen === 3 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Speech Timer and Controls */}
            <div className={`${cardClass} rounded-xl shadow-pp p-6`} data-tour="elections-speech-timer">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Mic size={20} className="text-pp-accent" />
                Candidate Speeches
              </h2>

              <div className="text-center mb-6">
                <div className={`text-6xl font-mono font-bold mb-2 ${
                  speakerTime > 60 ? 'text-green-600' :
                  speakerTime > 30 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {formatTime(speakerTime)}
                </div>
                <div className="text-sm text-gray-500 mb-4">
                  {currentCandidate ? `${currentCandidate.name} speaking` : 'No speaker active'}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <label className="text-sm font-medium">Speech Time (min):</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={electionSetup.speechTimeLimit}
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value) || 3;
                      setElectionSetup(prev => ({ ...prev, speechTimeLimit: newLimit }));
                      setCandidateSpeechTimeLimit(newLimit);
                      if (!currentCandidate || !isTimerRunning) {
                        setSpeakerTime(newLimit * 60);
                      }
                    }}
                    className="w-20 p-2 border rounded"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setIsTimerRunning(!isTimerRunning)}
                    disabled={!currentCandidate}
                    className="flex flex-1 items-center justify-center gap-2 rounded bg-pp-accent px-4 py-2 text-white hover:bg-pp-accentHover disabled:opacity-50"
                  >
                    {isTimerRunning ? <Pause size={16} /> : <Play size={16} />}
                    {isTimerRunning ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    onClick={nextCandidate}
                    disabled={!currentCandidate}
                    className="flex flex-1 items-center justify-center gap-2 rounded bg-pp-sage px-4 py-2 text-white hover:opacity-90 disabled:opacity-50"
                  >
                    <SkipForward size={16} />
                    Next Speaker
                  </button>
                </div>

                <button
                  data-tour="elections-begin-voting"
                  onClick={beginVoting}
                  disabled={electionNominees.filter(n => n.status === 'accepted' && !n.hasSpoken).length > 0}
                  className="w-full rounded-lg bg-pp-accent px-4 py-3 font-medium text-white hover:bg-pp-accentHover disabled:opacity-50"
                >
                  Begin Voting
                </button>
              </div>
            </div>

            {/* Candidates List */}
            <div className={`${cardClass} rounded-xl shadow-pp p-6`} data-tour="elections-candidates">
              <h3 className="text-xl font-bold mb-4">Candidates</h3>

              <div className="space-y-3">
                {electionNominees.filter(n => n.status === 'accepted').map(nominee => (
                  <div key={nominee.id} className={`p-3 rounded ${accentClass} ${
                    currentCandidate?.id === nominee.id ? 'ring-2 ring-pp-accent' : ''
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{nominee.name}</span>
                        {nominee.hasSpoken && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            ✓ Spoken
                          </span>
                        )}
                      </div>
                      {nominee.hasSpoken ? (
                        <span className="px-3 py-1 bg-gray-200 text-gray-600 rounded text-sm">
                          Complete
                        </span>
                      ) : (
                        <button
                          onClick={() => startCandidateSpeech(nominee)}
                          disabled={currentCandidate?.id === nominee.id}
                          className="px-3 py-1 bg-pp-accent text-white rounded text-sm hover:bg-pp-accentHover disabled:opacity-50"
                        >
                          {currentCandidate?.id === nominee.id ? 'Speaking' : 'Start Speech'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Screen 4: Voting */}
        {electionScreen === 4 && (
          <div className="space-y-6" data-tour="elections-ballot">
            <div className={`${cardClass} rounded-xl shadow-pp p-6`}>
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Award size={20} className="text-pp-accent" />
                Record ballot results — {officeLabel}
              </h2>

              {onOpenRemoteElectionBallot && (
                <div className="mb-6 rounded-xl border border-pp-line bg-pp-paper p-4" data-tour="elections-remote-ballot">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="font-medium">Remote ballot</h3>
                    {remoteVoteSession?.status === 'open' && remoteVoteSession?.kind === 'election' ? (
                      <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                        Open · {remoteVoteTally?.total ?? 0} ballots
                      </span>
                    ) : remoteVoteSession?.status === 'closed' && remoteVoteSession?.kind === 'election' ? (
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Closed · {remoteVoteTally?.total ?? 0} ballots
                      </span>
                    ) : null}
                  </div>
                  {remoteVoteError ? (
                    <p className="mb-2 text-sm text-rose-600">{remoteVoteError}</p>
                  ) : null}
                  {remoteVoteSession?.kind === 'election' &&
                  (remoteVoteSession?.status === 'open' || remoteVoteSession?.status === 'closed') ? (
                    <div className="mb-3 space-y-1 text-sm">
                      {electionNominees
                        .filter((n) => n.status === 'accepted')
                        .map((n) => (
                          <div key={n.id} className="flex justify-between">
                            <span>{n.name}</span>
                            <span className="font-semibold">
                              {remoteVoteTally?.counts?.[String(n.id)] ?? 0}
                            </span>
                          </div>
                        ))}
                      {(remoteVoteTally?.writeIns || []).map((w) => (
                        <div key={`write-in-${w.text}`} className="flex justify-between italic text-gray-600">
                          <span>{w.text} (write-in)</span>
                          <span className="font-semibold">{w.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {remoteVoteSession?.status === 'closed' && remoteVoteSession?.kind === 'election' ? (
                    <p className="mb-2 text-xs text-pp-muted">
                      Ballot closed — tallies above are final and already applied below.
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    {remoteVoteSession?.status === 'open' && remoteVoteSession?.kind === 'election' ? (
                      <button
                        type="button"
                        disabled={remoteVoteBusy}
                        onClick={() => onCloseRemoteVote?.()}
                        className="rounded-lg bg-pp-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                      >
                        Close remote ballot
                      </button>
                    ) : remoteVoteSession?.status === 'closed' && remoteVoteSession?.kind === 'election' ? (
                      <>
                        <button
                          type="button"
                          disabled={remoteVoteBusy}
                          onClick={() => onUseRemoteElectionTally?.()}
                          className="rounded-lg bg-pp-sage px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                        >
                          Use final tallies
                        </button>
                        <button
                          type="button"
                          disabled={remoteVoteBusy || !electionNominees.some((n) => n.status === 'accepted')}
                          onClick={() => onOpenRemoteElectionBallot?.()}
                          className="rounded-lg border border-pp-line bg-white px-4 py-2 text-sm hover:bg-pp-surface disabled:opacity-50"
                          title="Start a new remote ballot"
                        >
                          Reopen new ballot
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={remoteVoteBusy || !electionNominees.some((n) => n.status === 'accepted')}
                        onClick={() => onOpenRemoteElectionBallot?.()}
                        className="rounded-lg bg-pp-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                      >
                        Open remote ballot
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Nominees */}
                <div>
                  <h3 className="font-medium mb-4">Official Nominees</h3>
                  <div className="space-y-3">
                    {electionNominees.filter(n => n.status === 'accepted').map(nominee => (
                      <div key={nominee.id} className={`p-3 rounded ${accentClass}`}>
                        <div className="flex items-center justify-between">
                          <label className="font-medium">{nominee.name}</label>
                          <input
                            type="number"
                            min="0"
                            value={electionBallotVotes[nominee.id] || ''}
                            onChange={(e) => handleVoteChange(nominee.id, e.target.value)}
                            className="w-24 p-2 border rounded text-center"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Write-ins and Invalid Ballots */}
                <div className="space-y-4">
                  {electionSetup.allowWriteIns && (
                    <div>
                      <h3 className="font-medium mb-3">Write-in Candidates</h3>
                      <div className="space-y-2">
                        {electionWriteInCandidates.map(candidate => (
                          <div key={candidate.id} className={`p-2 rounded ${accentClass} flex items-center justify-between`}>
                            <span className="text-sm">{candidate.name}</span>
                            <span className="text-sm font-medium">{candidate.votes} votes</span>
                          </div>
                        ))}
                        <button
                          onClick={addWriteInCandidate}
                          className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded text-sm text-gray-600 hover:border-gray-400"
                        >
                          + Add Write-in Candidate
                        </button>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="font-medium mb-3">Invalid/Illegible Ballots</h3>
                    <input
                      type="number"
                      min="0"
                      value={electionInvalidBallots}
                      onChange={(e) => setElectionInvalidBallots(parseInt(e.target.value) || 0)}
                      className="w-full p-3 border rounded text-center"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Total votes: {Object.values(electionBallotVotes).reduce((sum, votes) => sum + votes, 0) +
                                  electionWriteInCandidates.reduce((sum, c) => sum + c.votes, 0) + electionInvalidBallots}
                  </div>
                  <button
                    data-tour="elections-complete"
                    onClick={completeElection}
                    className="px-6 py-2 bg-pp-accent text-white rounded-lg hover:bg-pp-accentHover font-medium"
                  >
                    Complete Election
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Screen 5: Results */}
        {electionScreen === 5 && (
          <div className="space-y-6" data-tour="elections-results">
            <div className={`${cardClass} rounded-xl shadow-pp p-6`}>
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Award size={20} className="text-pp-accent" />
                Election results — {officeLabel}
              </h2>

              {/* Winner Announcement */}
              {(() => {
                const winners = getElectionWinnersLocal();
                const seatsToFill = electionSetup.seatsToFill || 1;

                return winners.length > 0 ? (
                  <div className="mb-6 rounded-xl border border-pp-sage/30 bg-pp-sageSoft p-6 text-center">
                    <h3 className="mb-2 text-2xl font-bold text-pp-sage">Election complete</h3>
                    {seatsToFill === 1 ? (
                      <>
                        <div className="text-xl">
                          <span className="font-bold">{winners[0].name}</span> has been elected as {officeLabel}
                        </div>
                        <div className="mt-1 text-lg text-pp-muted">
                          with {winners[0].votes} vote{winners[0].votes !== 1 ? 's' : ''}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-xl mb-3">
                          The following candidates have been elected to {officeLabel}:
                        </div>
                        <div className="space-y-2">
                          {winners.map((winner, index) => (
                            <div key={index} className="text-lg">
                              <span className="font-bold">{winner.name}</span> - {winner.votes} vote{winner.votes !== 1 ? 's' : ''}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="mb-6 p-6 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                    <h3 className="text-xl font-bold text-yellow-800 mb-2">Election Complete</h3>
                    <div className="text-lg text-yellow-700">
                      No candidates received votes
                    </div>
                  </div>
                );
              })()}

              {/* Vote Totals - Only show breakdown if enabled */}
              {electionSetup.showVoteBreakdown ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium mb-3">Official Nominees</h3>
                    <div className="space-y-2">
                      {electionNominees.filter(n => n.status === 'accepted').map(nominee => (
                        <div key={nominee.id} className={`p-3 rounded ${accentClass} flex items-center justify-between`}>
                          <span className="font-medium">{nominee.name}</span>
                          <span className={`font-bold ${getElectionWinner()?.name === nominee.name ? 'text-green-600' : ''}`}>
                            {electionBallotVotes[nominee.id] || 0} votes
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    {electionWriteInCandidates.length > 0 && (
                      <div>
                        <h3 className="font-medium mb-3">Write-in Candidates</h3>
                        <div className="space-y-2">
                          {electionWriteInCandidates.map(candidate => (
                            <div key={candidate.id} className={`p-2 rounded ${accentClass} flex items-center justify-between`}>
                              <span className="text-sm">{candidate.name}</span>
                              <span className={`text-sm font-bold ${getElectionWinner()?.name === candidate.name ? 'text-green-600' : ''}`}>
                                {candidate.votes} votes
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className={`p-3 rounded ${accentClass}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span>Invalid/Illegible Ballots</span>
                        <span className="font-medium">{electionInvalidBallots}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-600 text-sm">
                    Vote breakdown hidden per election settings
                  </p>
                </div>
              )}

              {/* Total Vote Count - Always show */}
              <div className="mt-6 rounded-lg border border-pp-accent/20 bg-pp-accentSoft p-3">
                <div className="flex items-center justify-between font-medium text-pp-ink">
                  <span>Total votes cast</span>
                  <span className="text-pp-accent">
                    {Object.values(electionBallotVotes).reduce((sum, votes) => sum + votes, 0) +
                     electionWriteInCandidates.reduce((sum, c) => sum + c.votes, 0) + electionInvalidBallots}
                  </span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center gap-4">
                  <button
                    onClick={returnToMeeting}
                    className="flex items-center gap-2 rounded-lg bg-pp-sage px-6 py-3 font-medium text-white hover:opacity-90"
                  >
                    Return to Meeting
                  </button>
                  <button
                    onClick={resetElection}
                    className="rounded-lg bg-pp-accent px-6 py-3 font-medium text-white hover:bg-pp-accentHover"
                  >
                    Start New Election
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ElectionsInterface;